import { ApiPath } from '@common/app/api-path.ts';

import {
  type ActivateVipStatusBuyLinkResponseType,
  type ActivateVipStatusResponseType,
} from '@/common/types/query/user.ts';
import {
  type ActivatePromoCodeResponseType,
  type ArenaOffersType,
  type ArenaPackRewardDto,
  type BoxList,
  type GetArenaOffersRequestType,
  type MarketBoxes,
} from '@/common/types/shop/marketBoxes.ts';

import { api } from '../base.ts';

const shopApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getShopInfo: builder.query<BoxList[], void>({
      query: () => ApiPath.SHOP_INFO,
      keepUnusedDataFor: 0,
    }),
    getArenaOffers: builder.query<GetArenaOffersRequestType, void>({
      query: () => '/shop/arena/offers',
      keepUnusedDataFor: 0,
    }),
    shopBuy: builder.mutation<BoxList, { name: MarketBoxes }>({
      query: ({ name }) => ({
        url: ApiPath.SHOP_BUY,
        method: 'POST',
        body: { type: name },
      }),
    }),
    shopShuffle: builder.mutation<BoxList, { type: MarketBoxes }>({
      query: ({ type }) => ({
        url: `${ApiPath.SHOP_SHUFFLE}/${type}`,
        method: 'POST',
      }),
    }),
    buyArenaOffer: builder.mutation<
      ActivateVipStatusResponseType | ActivateVipStatusBuyLinkResponseType,
      { offerId: string; currency: 'XTR' | 'TON' | 'USDT' }
    >({
      query: ({ offerId, currency }) => ({
        url: `/league/arena/purchase/initiate/${offerId}`,
        method: 'POST',
        body: { currency },
      }),
    }),
    claimProfitDrop: builder.mutation<void, { offerId: string }>({
      query: ({ offerId }) => ({
        url: `/league/arena/claim-profit-drop/${offerId}`,
        method: 'POST',
      }),
    }),
    shuffleArenaOffer: builder.mutation<
      { id: string; reward: ArenaPackRewardDto },
      { id: string; body: ArenaPackRewardDto }
    >({
      query: ({ id, body }) => ({
        url: `/shop/arenaOffer/shuffle/${id}`,
        method: 'POST',
        body,
      }),
    }),
    showPoput: builder.mutation<
      void,
      { leagueId: string; type: ArenaOffersType }
    >({
      query: (body) => ({
        url: '/shop/arenaOffer/popupShown',
        method: 'POST',
        body,
      }),
    }),
    activatePromoCode: builder.mutation<
      ActivatePromoCodeResponseType,
      { code: string }
    >({
      query: ({ code }) => ({
        url: ApiPath.PROMO_ACTIVATE + code,
        method: 'POST',
      }),
    }),
  }),
  overrideExisting: false,
});
const {
  useShowPoputMutation,
  useGetArenaOffersQuery,
  useLazyGetArenaOffersQuery,
  useGetShopInfoQuery,
  useShopBuyMutation,
  useShopShuffleMutation,
  useBuyArenaOfferMutation,
  useShuffleArenaOfferMutation,
  useActivatePromoCodeMutation,
  useClaimProfitDropMutation,
} = shopApi;

export {
  shopApi,
  useActivatePromoCodeMutation,
  useBuyArenaOfferMutation,
  useClaimProfitDropMutation,
  useGetArenaOffersQuery,
  useGetShopInfoQuery,
  useLazyGetArenaOffersQuery,
  useShopBuyMutation,
  useShopShuffleMutation,
  useShowPoputMutation,
  useShuffleArenaOfferMutation,
};
